import { Box, Skeleton, Stack, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { Section } from '../../ui/Section';

export type InsightTileVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'purple';

export interface InsightTileProps {
  label: ReactNode;
  value: ReactNode;
  description: ReactNode;
  icon: ReactNode;
  variant: InsightTileVariant;
  loading?: boolean;
  sx?: SxProps<Theme>;
}

const getAccentPalette = (theme: Theme, variant: InsightTileVariant) => {
  switch (variant) {
    case 'primary':
      return theme.palette.primary;
    case 'secondary':
      return theme.palette.secondary;
    case 'success':
      return theme.palette.success;
    case 'warning':
      return theme.palette.warning;
    case 'error':
      return theme.palette.error;
    case 'info':
      return theme.palette.custom.blue;
    case 'purple':
      return theme.palette.custom.violet;
    default:
      return theme.palette.custom.gray;
  }
};

export const InsightTile = ({
  label,
  value,
  description,
  icon,
  variant,
  loading = false,
  sx,
}: InsightTileProps) => {
  const theme = useTheme();
  const accentPalette = getAccentPalette(theme, variant);

  return (
    <Box
      sx={{ height: '100%', ...sx }}
      aria-busy={loading}
      data-variant={variant}
    >
      <Section style={{ p: 4, mb: 0, height: '100%' }}>
        {loading ? (
          <Stack
            spacing={1}
            aria-hidden="true"
          >
            <Skeleton
              variant="text"
              width="55%"
              height={20}
            />
            <Skeleton
              variant="text"
              width="70%"
              height={36}
            />
            <Skeleton
              variant="text"
              width="85%"
              height={20}
            />
          </Stack>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 3,
              height: '100%',
              minWidth: 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  color: theme.palette.custom.gray[600],
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  mb: 1,
                  fontWeight: 500,
                }}
              >
                {label}
              </Box>
              <Box
                sx={{
                  color: theme.palette.custom.gray[900],
                  fontSize: '20px',
                  mb: 1,
                  fontWeight: 600,
                  overflowWrap: 'anywhere',
                }}
              >
                {value}
              </Box>
              {description !== undefined && description !== null && (
                <Box
                  sx={{
                    color: theme.palette.custom.gray[600],
                    fontSize: '12px',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {description}
                </Box>
              )}
            </Box>

            {icon !== undefined && icon !== null && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: alpha(accentPalette[700], 0.125),
                  color: accentPalette[700],
                  '& svg': {
                    width: 24,
                    height: 24,
                  },
                }}
              >
                {icon}
              </Box>
            )}
          </Box>
        )}
      </Section>
    </Box>
  );
};
